import { useState } from 'react'
import { Button, Modal, Select } from '@/components/ui'
import { useFreightHealth } from '../api/queries'
import type { QuoteRequestBody } from '../api/types'

// Standard NMFC freight classes. Required by every carrier we quote through
// (TQL rejects a quote outright without one; Echo Global's /v2/rates also
// expects a classification per commodity).
const FREIGHT_CLASSES = [
  '50', '55', '60', '65', '70', '77.5', '85', '92.5', '100', '110',
  '125', '150', '175', '200', '250', '300', '400', '500',
]

export interface QuoteRequestFormProps {
  open: boolean
  defaultProvider?: string | null
  isPending: boolean
  onClose: () => void
  onSubmit: (body: QuoteRequestBody) => void
}

/**
 * Logistics overhaul Phase 2b — request carrier quotes for a pipeline row.
 *
 * Single commodity line + single provider per request (v1 — matches the
 * backend's current single-provider quote endpoint; comparing multiple
 * providers means running this twice). Origin/destination/commodities
 * entered here are also reused for the tender call so the BOL carries the
 * same data the quote was priced against.
 */
export function QuoteRequestForm({
  open,
  defaultProvider,
  isPending,
  onClose,
  onSubmit,
}: QuoteRequestFormProps) {
  const { data: health } = useFreightHealth()
  const enabledProviders = (health ?? []).filter((h) => h.enabled)

  const [provider, setProvider] = useState(defaultProvider ?? '')
  const [originCity, setOriginCity] = useState('')
  const [originState, setOriginState] = useState('')
  const [originZip, setOriginZip] = useState('')
  const [destCity, setDestCity] = useState('')
  const [destState, setDestState] = useState('')
  const [destZip, setDestZip] = useState('')
  const [description, setDescription] = useState('')
  const [weightLbs, setWeightLbs] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [freightClass, setFreightClass] = useState('70')
  const [pickupDate, setPickupDate] = useState('')

  const canSubmit =
    !!provider &&
    !!originCity && !!originState && !!originZip &&
    !!destCity && !!destState && !!destZip &&
    !!description && !!weightLbs && Number(weightLbs) > 0 && !!freightClass

  if (!open) return null

  return (
    <Modal
      open={open}
      onOpenChange={(next) => !next && onClose()}
      tone="info"
      title="Get a freight quote"
      description="Requests rates from one carrier at a time. Compare by running this again with a different carrier."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isPending}
            disabled={!canSubmit}
            onClick={() =>
              onSubmit({
                provider,
                origin: { city: originCity, state: originState, zipcode: originZip },
                destination: { city: destCity, state: destState, zipcode: destZip },
                commodities: [
                  {
                    description,
                    weight_lbs: Number(weightLbs),
                    quantity: Number(quantity) || 1,
                    freight_class: freightClass,
                  },
                ],
                pickup_date: pickupDate || undefined,
              })
            }
          >
            Get quote
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Carrier</span>
          <Select value={provider} onChange={(e) => setProvider(e.target.value)}>
            <option value="">Choose a carrier…</option>
            {enabledProviders.map((p) => (
              <option key={p.provider} value={p.provider}>
                {p.display_name}
              </option>
            ))}
          </Select>
        </label>

        <div>
          <span className="mb-1 block text-sm text-secondary">Origin</span>
          <div className="grid grid-cols-3 gap-2">
            <TextField placeholder="City" value={originCity} onChange={setOriginCity} />
            <TextField placeholder="State" value={originState} onChange={setOriginState} maxLength={2} />
            <TextField placeholder="ZIP" value={originZip} onChange={setOriginZip} />
          </div>
        </div>

        <div>
          <span className="mb-1 block text-sm text-secondary">Destination</span>
          <div className="grid grid-cols-3 gap-2">
            <TextField placeholder="City" value={destCity} onChange={setDestCity} />
            <TextField placeholder="State" value={destState} onChange={setDestState} maxLength={2} />
            <TextField placeholder="ZIP" value={destZip} onChange={setDestZip} />
          </div>
        </div>

        <div>
          <span className="mb-1 block text-sm text-secondary">Commodity</span>
          <div className="grid grid-cols-4 gap-2">
            <TextField
              placeholder="Description"
              value={description}
              onChange={setDescription}
              className="col-span-1"
            />
            <TextField
              placeholder="Weight (lbs)"
              value={weightLbs}
              onChange={setWeightLbs}
              type="number"
            />
            <TextField
              placeholder="Quantity"
              value={quantity}
              onChange={setQuantity}
              type="number"
            />
            <Select value={freightClass} onChange={(e) => setFreightClass(e.target.value)}>
              {FREIGHT_CLASSES.map((c) => (
                <option key={c} value={c}>
                  Class {c}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Pickup date (optional)</span>
          <input
            type="date"
            value={pickupDate}
            onChange={(e) => setPickupDate(e.target.value)}
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary focus:border-border-focus focus:outline-none"
          />
        </label>
      </div>
    </Modal>
  )
}

function TextField({
  value,
  onChange,
  placeholder,
  type = 'text',
  maxLength,
  className,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
  maxLength?: number
  className?: string
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className={`rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none ${className ?? ''}`}
    />
  )
}
